import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Calendar, Clock, Trash2, Plus, HelpCircle } from 'lucide-react';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { SeasonRolloverNotificationPreferences } from '@/components/notifications/SeasonRolloverNotificationPreferences';
import { seasonRolloverNotificationService } from '@/services/seasonRolloverNotificationService';

interface ScheduledRollover {
  id: string;
  entity_types: string[];
  entity_ids: string[];
  old_season_name: string;
  new_season_name: string;
  scheduled_for: string;
  notes: string | null;
  execution_status: string;
  executed_at: string | null;
  execution_error: string | null;
  send_notifications: boolean;
  created_at: string;
}

interface EntityRolloverSchedulerProps {
  entityType: 'team' | 'venue' | 'coach';
  entityIds: string[];
  entityName?: string;
}

export const EntityRolloverScheduler = ({ 
  entityType, 
  entityIds,
  entityName = 'My Entities'
}: EntityRolloverSchedulerProps) => {
  const { user } = useAuthenticatedUser();
  const [scheduledRollovers, setScheduledRollovers] = useState<ScheduledRollover[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [oldSeasonName, setOldSeasonName] = useState('');
  const [newSeasonName, setNewSeasonName] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadScheduledRollovers();
  }, [entityIds]);

  const loadScheduledRollovers = async () => {
    if (!entityIds.length) return;
    
    try {
      const { data, error } = await supabase
        .from('scheduled_season_rollovers')
        .select('*')
        .not('entity_ids', 'is', null)
        .contains('entity_types', [entityType])
        .order('scheduled_for', { ascending: true });

      if (error) throw error;
      
      // Filter to only show rollovers that include our entity IDs
      const filtered = (data || []).filter((rollover: any) => 
        rollover.entity_ids && 
        entityIds.some((id: string) => rollover.entity_ids.includes(id))
      );
      
      setScheduledRollovers(filtered as unknown as ScheduledRollover[]);
    } catch (error) {
      console.error('Error loading scheduled rollovers:', error);
      toast.error('Failed to load scheduled rollovers');
    }
  };

  const handleScheduleRollover = async () => {
    if (!user?.id || !oldSeasonName || !newSeasonName || !scheduledDate || !scheduledTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const scheduledFor = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();

      const { error } = await supabase
        .from('scheduled_season_rollovers')
        .insert({
          entity_types: [entityType],
          entity_ids: entityIds,
          old_season_name: oldSeasonName,
          new_season_name: newSeasonName,
          scheduled_for: scheduledFor,
          scheduled_by: user.id,
          scheduled_by_type: 'supabase',
          notes: notes || null,
          send_notifications: true,
        });

      if (error) throw error;

      // Send notification
      await seasonRolloverNotificationService.notifyRolloverScheduled(
        user.id,
        'supabase',
        entityType,
        entityIds[0],
        oldSeasonName,
        newSeasonName,
        scheduledFor
      );

      toast.success('Season rollover scheduled successfully!');
      setShowForm(false);
      resetForm();
      loadScheduledRollovers();
    } catch (error) {
      console.error('Error scheduling rollover:', error);
      toast.error('Failed to schedule season rollover');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRollover = async (id: string) => {
    try {
      const { error } = await supabase
        .from('scheduled_season_rollovers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Scheduled rollover deleted');
      loadScheduledRollovers();
    } catch (error) {
      console.error('Error deleting rollover:', error);
      toast.error('Failed to delete scheduled rollover');
    }
  };

  const resetForm = () => {
    setOldSeasonName('');
    setNewSeasonName('');
    setScheduledDate('');
    setScheduledTime('');
    setNotes('');
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
      pending: { variant: "secondary", label: "Pending" },
      executing: { variant: "default", label: "Executing" },
      completed: { variant: "outline", label: "Completed" },
      failed: { variant: "destructive", label: "Failed" },
      cancelled: { variant: "outline", label: "Cancelled" },
    };
    
    const config = variants[status] || variants.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <CardTitle>Season Rollover Scheduler</CardTitle>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-primary cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs p-4" side="right">
                    <p className="font-semibold mb-2">What is a Season Rollover?</p>
                    <p className="text-sm mb-2">
                      A Season Rollover transitions your team from one sports season to 
                      the next (e.g., "2024 Season" to "2025 Season").
                    </p>
                    <p className="text-sm font-medium mb-1">What happens:</p>
                    <ul className="text-sm list-disc ml-4 space-y-1">
                      <li>Current season stats are archived</li>
                      <li>New season starts with fresh statistics</li>
                      <li>Team members are automatically migrated</li>
                      <li>All historical data is preserved</li>
                    </ul>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <CardDescription>
              Schedule automatic season transitions for {entityName}
            </CardDescription>
          </div>
          {!showForm && (
            <Button onClick={() => setShowForm(true)} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Schedule Rollover
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showForm && (
          <Card className="border-primary/20 bg-muted/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">New Scheduled Rollover</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="oldSeasonName">Current Season Name</Label>
                  <Input
                    id="oldSeasonName"
                    value={oldSeasonName}
                    onChange={(e) => setOldSeasonName(e.target.value)}
                    placeholder="e.g., 2024 Season"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newSeasonName">New Season Name</Label>
                  <Input
                    id="newSeasonName"
                    value={newSeasonName}
                    onChange={(e) => setNewSeasonName(e.target.value)}
                    placeholder="e.g., 2025 Season"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="scheduledDate">Scheduled Date</Label>
                  <Input
                    id="scheduledDate"
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="scheduledTime">Scheduled Time</Label>
                  <Input
                    id="scheduledTime"
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this rollover..."
                  rows={3}
                />
              </div>

              <Button 
                onClick={handleScheduleRollover} 
                disabled={loading}
                className="w-full"
              >
                <Clock className="w-4 h-4 mr-2" />
                {loading ? 'Scheduling...' : 'Schedule Rollover'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Scheduled Rollovers List */}
        {scheduledRollovers.length > 0 ? (
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Scheduled Rollovers</h4>
            {scheduledRollovers.map((rollover) => (
              <Card key={rollover.id} className="border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {rollover.old_season_name} → {rollover.new_season_name}
                        </span>
                        {getStatusBadge(rollover.execution_status)}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(rollover.scheduled_for), 'MMM d, yyyy')}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {format(new Date(rollover.scheduled_for), 'h:mm a')}
                        </div>
                      </div>

                      {rollover.notes && (
                        <p className="text-sm text-muted-foreground italic">
                          {rollover.notes}
                        </p>
                      )}

                      {rollover.execution_error && (
                        <p className="text-sm text-destructive">
                          Error: {rollover.execution_error}
                        </p>
                      )}
                    </div>

                    {rollover.execution_status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRollover(rollover.id)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          !showForm && (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No scheduled rollovers yet</p>
              <p className="text-sm">Schedule automatic season transitions for {entityName}</p>
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
};
